import React from 'react';
import { useDispatch } from 'react-redux';
import { TextField, FormHelperText, Button, CircularProgress } from '@mui/material';
import { BorderColor } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PlatformCard from '../../../../../../../../components/Cards/PlatformCard';
import RedirectToTicket from '../../../../../../../../components/Dialog/RedirectToTicket';
import useOnlineLocation from '../../../hooks/useOnlineLocation';
import { previousTab } from '../../../../../../../../app/store/slices/event/event';
import { PLATFORM_NOT_DECIDED } from '../../../../../../../../app/store/constant';
import { H6 } from '../../../../../../../../themes/typography';
import '../../../../../../../../styles/components/OnlineLocation.css';

function OnlineLocation() {
    const dispatch = useDispatch();

    const {
        loading,
        formik,
        platforms,
        toolbarOptions,
        isEditModeOn,
        setIsEditModeOn,
        openTicketPopup,
        setOpenTicketPopup,
        handleTicketRedirect,
        handleSkipForNow,
        isDiffMoreThan24Hours,
        shouldShowEditButton
    } = useOnlineLocation();

    const disabledFields = formik.values.platformName === PLATFORM_NOT_DECIDED || !isEditModeOn;

    return (
        <React.Fragment>
            <form noValidate className="online-location-container" onSubmit={formik.handleSubmit}>
                <div className="online-location-body-container">
                    {shouldShowEditButton && (
                        <div style={editBtnStyle}>
                            <Button
                                startIcon={<BorderColor />}
                                variant="text"
                                onClick={() => setIsEditModeOn(true)}
                                disabled={isEditModeOn}
                            >
                                Edit
                            </Button>
                        </div>
                    )}

                    <div className="online-location-form-starts">
                        <div className="platform-selection-container">
                            <H6>Select Platform *</H6>
                            <div className="platform-listing-container">
                                {platforms
                                    .filter(
                                        (platform) =>
                                            isDiffMoreThan24Hours ||
                                            platform.platformName !== PLATFORM_NOT_DECIDED
                                    )
                                    .map((platform, index) => (
                                        <PlatformCard
                                            key={index}
                                            img={platform.img}
                                            platformName={platform.platformName}
                                            isSelected={
                                                formik.values.platformName === platform.platformName
                                            }
                                            onCardClick={() => {
                                                if (!isEditModeOn) return false;
                                                formik.setFieldValue(
                                                    'platformName',
                                                    platform.platformName
                                                );
                                            }}
                                            style={{
                                                width:
                                                    platform.platformName === PLATFORM_NOT_DECIDED
                                                        ? 'auto'
                                                        : '110px',
                                                cursor: isEditModeOn ? 'pointer' : 'not-allowed'
                                            }}
                                        />
                                    ))}
                            </div>
                            <FormHelperText
                                error={
                                    formik.touched.platformName &&
                                    Boolean(formik.errors.platformName)
                                }
                            >
                                {formik.touched.platformName && formik.errors.platformName}
                            </FormHelperText>
                        </div>
                        <div className="joining-link-container">
                            <TextField
                                id="joining-link"
                                name="joining-link"
                                label="Joining Link *"
                                disabled={disabledFields}
                                placeholder="Enter the joining link"
                                fullWidth
                                value={formik.values.joiningLink}
                                onChange={(e) =>
                                    formik.setFieldValue('joiningLink', e.target.value)
                                }
                                error={
                                    formik.touched.joiningLink && Boolean(formik.errors.joiningLink)
                                }
                                helperText={
                                    formik.touched.joiningLink && formik.errors.joiningLink
                                        ? formik.errors.joiningLink
                                        : 'This link will be shared with the attendees on purchase or booking of a ticket.'
                                }
                            />
                            <TextField
                                fullWidth
                                id="joining-credentials"
                                name="joining-credentials"
                                label="Joining Credentials"
                                disabled={disabledFields}
                                placeholder="Enter the joining credentials"
                                value={formik.values.joiningCredentials}
                                onChange={(e) => {
                                    formik.setFieldValue('joiningCredentials', e.target.value);
                                }}
                                error={
                                    formik.touched.joiningCredentials &&
                                    Boolean(formik.errors.joiningCredentials)
                                }
                                helperText={
                                    formik.touched.joiningCredentials &&
                                    formik.errors.joiningCredentials
                                        ? formik.errors.joiningCredentials
                                        : 'These credentials will be shared with the attendees on purchase or booking of a ticket.'
                                }
                            />
                        </div>
                        <div className="joining-instructions-container">
                            <H6>Joining Instructions *</H6>
                            <div className="quill-rich-text-editor">
                                <ReactQuill
                                    theme="snow"
                                    readOnly={disabledFields}
                                    id="joining-instructions"
                                    value={formik.values.joiningInstructions}
                                    modules={{ toolbar: toolbarOptions }}
                                    placeholder="Describe your joining instructions here..."
                                    onChange={(value) =>
                                        formik.setFieldValue('joiningInstructions', value)
                                    }
                                />
                                <FormHelperText
                                    error={
                                        formik.touched.joiningInstructions &&
                                        Boolean(formik.errors.joiningInstructions)
                                    }
                                >
                                    {formik.touched.joiningInstructions &&
                                        formik.errors.joiningInstructions}
                                </FormHelperText>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="submit-button-container">
                    <Button variant="text" onClick={() => dispatch(previousTab())}>
                        Back to Media
                    </Button>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        {/* Only show skip for now button if edit mode is false and platform name is not decided */}
                        {formik.values.platformName === PLATFORM_NOT_DECIDED && (
                            <Button variant="main" onClick={() => setOpenTicketPopup(true)}>
                                Skip for now
                            </Button>
                        )}
                        {formik.values.platformName !== PLATFORM_NOT_DECIDED && (
                            <Button variant="main" disabled={loading} type="submit">
                                {/* {isEditModeOn ? 'Save' : 'Next'} */}
                                Next{' '}
                                {loading && (
                                    <CircularProgress
                                        size={20}
                                        sx={{
                                            marginLeft: '0.75rem',
                                            color: 'var(--primary-color)'
                                        }}
                                    />
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
            <RedirectToTicket
                open={openTicketPopup}
                onClose={handleSkipForNow}
                onClick={handleTicketRedirect}
            />
        </React.Fragment>
    );
}

export default OnlineLocation;

const editBtnStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%'
};
